import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Dashboard
export const getDashboard = () => api.get('/dashboard');
export const getDashboardStats = () => api.get('/dashboard');

// Clients
export const getClients = () => api.get('/clients');
export const getClient = (id) => api.get(`/clients/${id}`);
export const getClientInvoices = (id) => api.get(`/clients/${id}/invoices`);
export const createClient = (data) => api.post('/clients', data);
export const updateClient = (id, data) => api.put(`/clients/${id}`, data);
export const deleteClient = (id) => api.delete(`/clients/${id}`);

// Products/Services
export const getProducts = () => api.get('/products');
export const createProduct = (data) => api.post('/products', data);
export const updateProduct = (id, data) => api.put(`/products/${id}`, data);
export const deleteProduct = (id) => api.delete(`/products/${id}`);

// Stock
export const getStockSummary = () => api.get('/stock/summary');
export const getStockAlerts = () => api.get('/stock/alerts');
export const getStockProfitability = () => api.get('/stock/profitability');
export const getStockMovements = (productId) => api.get('/stock/movements', { params: productId ? { product_id: productId } : {} });
export const createStockMovement = (data) => api.post('/stock/movement', data);
export const importStockCsv = (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/stock/import-csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); };

// Shopify Stock Sync
export const getShopifyProducts = () => api.get('/shopify/products');
export const importShopifyProducts = (productIds) => api.post('/shopify/products/import', { product_ids: productIds });
export const syncShopifyStock = () => api.post('/shopify/products/sync-stock');

// Portal
export const generatePortalLink = (invoiceId) => api.post('/portal/generate-link', { invoice_id: invoiceId });
export const getPortalStats = () => api.get('/portal/admin/stats');

// Projects
export const getProjects = (status) => api.get('/projects', { params: status ? { status } : {} });
export const getProject = (id) => api.get(`/projects/${id}`);
export const createProject = (data) => api.post('/projects', data);
export const updateProject = (id, data) => api.put(`/projects/${id}`, data);
export const deleteProject = (id) => api.delete(`/projects/${id}`);
export const getProjectDashboard = (id) => api.get(`/projects/${id}/dashboard`);
export const getProjectTasks = (id) => api.get(`/projects/${id}/tasks`);
export const createProjectTask = (id, data) => api.post(`/projects/${id}/tasks`, data);
export const updateProjectTask = (projectId, taskId, data) => api.put(`/projects/${projectId}/tasks/${taskId}`, data);
export const deleteProjectTask = (projectId, taskId) => api.delete(`/projects/${projectId}/tasks/${taskId}`);
export const getTimeEntries = (id) => api.get(`/projects/${id}/time`);
export const createTimeEntry = (id, data) => api.post(`/projects/${id}/time`, data);
export const deleteTimeEntry = (projectId, entryId) => api.delete(`/projects/${projectId}/time/${entryId}`);
export const updateMilestones = (id, milestones) => api.put(`/projects/${id}/milestones`, { milestones });
export const linkInvoiceToProject = (projectId, invoiceId) => api.post(`/projects/${projectId}/link-invoice`, { invoice_id: invoiceId });
export const linkExpenseToProject = (projectId, expenseId) => api.post(`/projects/${projectId}/link-expense`, { expense_id: expenseId });

// Project Collaboration
export const getProjectComments = (projectId, taskId) => api.get(`/projects/${projectId}/comments`, { params: taskId ? { task_id: taskId } : {} });
export const createProjectComment = (projectId, data) => api.post(`/projects/${projectId}/comments`, data);
export const deleteProjectComment = (projectId, commentId) => api.delete(`/projects/${projectId}/comments/${commentId}`);
export const getProjectFiles = (projectId) => api.get(`/projects/${projectId}/files`);
export const uploadProjectFile = (projectId, file) => { const fd = new FormData(); fd.append('file', file); return api.post(`/projects/${projectId}/files`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); };
export const deleteProjectFile = (projectId, fileId) => api.delete(`/projects/${projectId}/files/${fileId}`);
export const getProjectActivity = (projectId) => api.get(`/projects/${projectId}/activity`);

// Project Finance
export const getProjectFinance = (projectId) => api.get(`/projects/${projectId}/finance`);

// Project Planning (Phase 4)
export const getProjectPlanning = (projectId) => api.get(`/projects/${projectId}/planning`);

// Project Reports (Phase 6)
export const getProjectReportPdfUrl = (projectId) => `/projects/${projectId}/reports/pdf`;
export const getProjectReportExcelUrl = (projectId) => `/projects/${projectId}/reports/excel`;
export const getProfitabilityReportUrl = () => `/projects/reports/profitability`;

// Manual Revenues
export const getRevenues = (year) => api.get('/revenues', { params: year ? { year } : {} });
export const createRevenue = (data) => api.post('/revenues', data);
export const updateRevenue = (id, data) => api.put(`/revenues/${id}`, data);
export const deleteRevenue = (id) => api.delete(`/revenues/${id}`);
export const getRevenueSummary = (year) => api.get('/revenues/summary', { params: year ? { year } : {} });

// Manual Invoices
export const createManualInvoice = (data) => api.post('/manual-invoices', data);
export const getProjectsProfitability = () => api.get('/projects/analytics/profitability');

// Invoices
export const getInvoices = (params) => api.get('/invoices', { params });
export const getInvoice = (id) => api.get(`/invoices/${id}`);
export const createInvoice = (data) => api.post('/invoices', data);
export const updateInvoice = (id, data) => api.put(`/invoices/${id}`, data);
export const duplicateInvoice = (id) => api.post(`/invoices/${id}/duplicate`);
export const deleteInvoice = (id) => api.delete(`/invoices/${id}`);
export const downloadInvoicePdf = async (id, qrBill = true) => {
  const response = await api.get(`/invoices/${id}/pdf?qr_bill=${qrBill}`, { responseType: 'blob' });
  return response;
};
export const getQrBillStatus = () => api.get('/qr-bill/status');
export const getBankBalance = () => api.get('/bank-balance');
export const updateBankBalance = (data) => api.put('/bank-balance', data);
export const getStockValue = () => api.get('/stock-value');
export const updateStockValue = (data) => api.put('/stock-value', data);
export const exportInvoicesCsv = async () => {
  const response = await api.get('/invoices/export/csv', { responseType: 'blob' });
  return response;
};

// Quotes (Devis)
export const getQuotes = (params) => api.get('/quotes', { params });
export const getQuote = (id) => api.get(`/quotes/${id}`);
export const createQuote = (data) => api.post('/quotes', data);
export const convertQuoteToInvoice = (id) => api.post(`/quotes/${id}/convert`);
export const deleteQuote = (id) => api.delete(`/quotes/${id}`);
export const downloadQuotePdf = async (id) => {
  const response = await api.get(`/quotes/${id}/pdf`, { responseType: 'blob' });
  return response;
};

// Payments
export const getPayments = (invoiceId) => api.get('/payments', { params: { invoice_id: invoiceId } });
export const createPayment = (data) => api.post('/payments', data);
export const deletePayment = (id) => api.delete(`/payments/${id}`);

// Credit Notes (Avoirs)
export const getCreditNotes = (invoiceId) => api.get('/credit-notes', { params: { invoice_id: invoiceId } });
export const getAllCreditNotes = () => api.get('/credit-notes');
export const getCreditNote = (id) => api.get(`/credit-notes/${id}`);
export const createCreditNote = (data) => api.post('/credit-notes', data);
export const deleteCreditNote = (id) => api.delete(`/credit-notes/${id}`);
export const downloadCreditNotePdf = (id) => api.get(`/credit-notes/${id}/pdf`, { responseType: 'blob' });

// Client Stats
export const getClientStats = (id) => api.get(`/clients/${id}/stats`);

// Recurring Invoices (Factures récurrentes)
export const getRecurringInvoices = () => api.get('/recurring-invoices');
export const getRecurringInvoice = (id) => api.get(`/recurring-invoices/${id}`);
export const createRecurringInvoice = (data) => api.post('/recurring-invoices', data);
export const updateRecurringInvoice = (id, data) => api.put(`/recurring-invoices/${id}`, data);
export const deleteRecurringInvoice = (id) => api.delete(`/recurring-invoices/${id}`);
export const generateFromRecurring = (id) => api.post(`/recurring-invoices/${id}/generate`);
export const checkDueRecurringInvoices = () => api.get('/recurring-invoices/check-due');

// Shopify Integration
export const connectShopify = (store_url, api_key, access_token) => 
  api.post('/shopify/connect', null, { params: { store_url, api_key, access_token } });
export const disconnectShopify = () => api.post('/shopify/disconnect');
export const getShopifyStatus = () => api.get('/shopify/status');
export const getShopifyOrders = (status = 'any', limit = 50) => 
  api.get('/shopify/orders', { params: { status, limit } });
export const createInvoiceFromShopify = (orderId) => api.post(`/shopify/orders/${orderId}/create-invoice`);

// Reminders
export const getReminders = (invoiceId) => api.get('/reminders', { params: { invoice_id: invoiceId } });
export const createReminder = (data) => api.post('/reminders', data);
export const markReminderSent = (id) => api.put(`/reminders/${id}/mark-sent`);

// Settings
export const getSettings = () => api.get('/settings');
export const updateSettings = (data) => api.put('/settings', data);
export const getTemplates = () => api.get('/templates');

// Expenses
export const getExpenses = (params) => api.get('/expenses', { params });
export const getExpense = (id) => api.get(`/expenses/${id}`);
export const createExpense = (data) => api.post('/expenses', data);
export const updateExpense = (id, data) => api.put(`/expenses/${id}`, data);
export const deleteExpense = (id) => api.delete(`/expenses/${id}`);
export const getExpenseCategories = () => api.get('/expenses/categories');

// Fiscal Summary
export const getFiscalSummary = (year) => api.get(`/fiscal/summary?year=${year}`);
export const exportFiscalPdf = (year) => api.get(`/fiscal/export/pdf?year=${year}`, { responseType: 'blob' });
export const exportFiscalCsv = (year) => api.get(`/fiscal/export/csv?year=${year}`, { responseType: 'blob' });

// Receipt Upload
export const uploadReceipt = (expenseId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/expenses/${expenseId}/receipt`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const getReceiptUrl = (expenseId) => `${api.defaults.baseURL}/expenses/${expenseId}/receipt`;
export const deleteReceipt = (expenseId) => api.delete(`/expenses/${expenseId}/receipt`);

// Quote Duplication
export const duplicateQuote = (quoteId) => api.post(`/quotes/${quoteId}/duplicate`);
export const updateQuoteStatus = (quoteId, status) => api.put(`/quotes/${quoteId}/status`, { status });

// QR Bill
export const getQrBillUrl = (invoiceId) => `${api.defaults.baseURL}/invoices/${invoiceId}/qr-bill`;

// Payment Reminders
export const getReminderSettings = () => api.get('/settings/reminders');
export const updateReminderSettings = (data) => api.put('/settings/reminders', data);
export const getOverdueInvoices = () => api.get('/invoices/overdue');
export const sendReminder = (invoiceId) => api.post(`/invoices/${invoiceId}/send-reminder`);
export const getInvoiceReminders = (invoiceId) => api.get(`/invoices/${invoiceId}/reminders`);

// Trial & Subscription
export const getTrialStatus = () => api.get('/auth/trial-status');
export const getPricing = () => api.get('/pricing');

// Admin routes
export const adminGetUsers = () => api.get('/admin/users');
export const adminGetUser = (id) => api.get(`/admin/users/${id}`);
export const adminUpdateUser = (id, data) => api.put(`/admin/users/${id}`, data);
export const adminActivateUser = (id, subscriptionType) => api.post(`/admin/users/${id}/activate?subscription_type=${subscriptionType}`);
export const adminDeactivateUser = (id) => api.post(`/admin/users/${id}/deactivate`);
export const adminGrantFreeAccess = (id, plan) => api.post(`/admin/users/${id}/grant-free`, { plan });
export const adminRevokeFreeAccess = (id) => api.post(`/admin/users/${id}/revoke-free`);
export const adminGetStats = () => api.get('/admin/stats');
export const adminDeleteUser = (id) => api.delete(`/admin/users/${id}`);
export const adminResetPassword = (id, password) => api.post(`/admin/users/${id}/reset-password`, { password });

// SMTP / Email
export const saveSMTPSettings = (data) => api.post('/settings/smtp', data);
export const testSMTPSettings = (data) => api.post('/settings/smtp/test', data);
export const sendInvoiceEmail = (invoiceId, data) => api.post(`/invoices/${invoiceId}/send-email`, data);
export const sendQuoteEmail = (quoteId, data) => api.post(`/quotes/${quoteId}/send-email`, data);

// HR & Payroll
export const getCantons = () => api.get('/hr/cantons');
export const getCantonHolidays = (canton, year) => api.get(`/hr/cantons/${canton}/holidays/${year}`);
export const getEmployees = (params) => api.get('/hr/employees', { params });
export const getEmployee = (id) => api.get(`/hr/employees/${id}`);
export const createEmployee = (data) => api.post('/hr/employees', data);
export const updateEmployee = (id, data) => api.put(`/hr/employees/${id}`, data);
export const deleteEmployee = (id) => api.delete(`/hr/employees/${id}`);
export const getPayslips = (params) => api.get('/hr/payslips', { params });
export const getPayslip = (id) => api.get(`/hr/payslips/${id}`);
export const calculatePayslip = (data) => api.post('/hr/payslips/calculate', data);
export const createPayslip = (data) => api.post('/hr/payslips', data);
export const validatePayslip = (id) => api.put(`/hr/payslips/${id}/validate`);
export const downloadPayslipPdf = async (id) => {
  const response = await api.get(`/hr/payslips/${id}/pdf`, { responseType: 'blob' });
  return response;
};
export const getAbsences = (params) => api.get('/hr/absences', { params });
export const createAbsence = (data) => api.post('/hr/absences', data);
export const updateAbsenceStatus = (id, status) => api.put(`/hr/absences/${id}/status`, { status });
export const getSalaryCertificate = async (employeeId, year) => {
  const response = await api.get(`/hr/employees/${employeeId}/salary-certificate/${year}`, { responseType: 'blob' });
  return response;
};

// Support Tickets
export const getTickets = () => api.get('/tickets');
export const getTicket = (id) => api.get(`/tickets/${id}`);
export const createTicket = (data) => api.post('/tickets', data);
export const replyToTicket = (id, data) => api.post(`/tickets/${id}/reply`, data);
export const getUnreadTicketCount = () => api.get('/tickets/unread-count');

// Admin Tickets
export const adminGetTickets = (params) => api.get('/admin/tickets', { params });
export const adminGetTicket = (id) => api.get(`/admin/tickets/${id}`);
export const adminReplyTicket = (id, data) => api.post(`/admin/tickets/${id}/reply`, data);
export const adminUpdateTicketStatus = (id, data) => api.put(`/admin/tickets/${id}/status`, data);
export const adminGetUnreadCount = () => api.get('/admin/tickets/unread-count');

// Export PDFs
export const exportClientsPdf = () => api.get('/export/clients/pdf', { responseType: 'blob' });
export const exportExpensesPdf = (year) => api.get(`/export/expenses/pdf${year ? `?year=${year}` : ''}`, { responseType: 'blob' });

export default api;
